/**
 * Mobile Notifications Page (Day-15 Module 10)
 * 
 * Notification types:
 * - shift starting soon
 * - medication due
 * - open shift approved
 * - leave approved
 * - document request
 * - compliance expiring
 * - critical participant alert
 * - sync failure warning
 * 
 * Filters: ALL, SHIFTS, ALERTS, TASKS, SYSTEM
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Bell, BellOff, Check, CheckCheck, Trash2, RefreshCw,
  Clock, Pill, Calendar, FileText, AlertTriangle, Shield, Users, Wifi,
  ChevronRight, MoreVertical, Filter, Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNotifications, useNotificationCounts, useCacheInvalidation } from '@/hooks/useQueryCache';
import { MobileBottomNav } from '@/components/mobile/MobileNavigation';

// ─────────────────────────────────────────────────────────────────
// Notification Type Configuration
// ─────────────────────────────────────────────────────────────────

const NOTIFICATION_CONFIG = {
  // Shift related
  SHIFT_STARTING_SOON: { icon: Clock, color: 'blue', category: 'SHIFTS' },
  SHIFT_ASSIGNED: { icon: Calendar, color: 'blue', category: 'SHIFTS' },
  OPEN_SHIFT_PUBLISHED: { icon: Calendar, color: 'indigo', category: 'SHIFTS' },
  OPEN_SHIFT_APPROVED: { icon: Check, color: 'green', category: 'SHIFTS' },
  PICKUP_APPROVED: { icon: Check, color: 'green', category: 'SHIFTS' },
  
  // Alerts
  MEDICATION_DUE: { icon: Pill, color: 'amber', category: 'ALERTS' },
  CRITICAL_PARTICIPANT_ALERT: { icon: AlertTriangle, color: 'red', category: 'ALERTS' },
  COMPLIANCE_EXPIRING: { icon: Shield, color: 'orange', category: 'ALERTS' },
  SYNC_FAILURE_WARNING: { icon: Wifi, color: 'red', category: 'ALERTS' },
  
  // Tasks
  DOCUMENT_REQUEST: { icon: FileText, color: 'purple', category: 'TASKS' },
  INCIDENT_ASSIGNED: { icon: AlertTriangle, color: 'red', category: 'TASKS' },
  
  // System
  LEAVE_APPROVED: { icon: Check, color: 'green', category: 'SYSTEM' },
  TIMESHEET_APPROVED: { icon: Check, color: 'green', category: 'SYSTEM' },
  TIMESHEET_REJECTED: { icon: AlertTriangle, color: 'red', category: 'SYSTEM' },
  INVOICE_GENERATED: { icon: FileText, color: 'blue', category: 'SYSTEM' },
};

const FILTER_TABS = [
  { id: 'ALL', label: 'All', icon: Bell },
  { id: 'SHIFTS', label: 'Shifts', icon: Calendar },
  { id: 'ALERTS', label: 'Alerts', icon: AlertTriangle },
  { id: 'TASKS', label: 'Tasks', icon: FileText },
  { id: 'SYSTEM', label: 'System', icon: Users },
];

// ─────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────

function getNotificationConfig(type) {
  return NOTIFICATION_CONFIG[type] || { icon: Bell, color: 'gray', category: 'SYSTEM' };
}

function getTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

function getColorClasses(color) {
  const colorMap = {
    blue: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    green: 'bg-green-500/10 text-green-600 border-green-500/30',
    amber: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    orange: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
    red: 'bg-red-500/10 text-red-600 border-red-500/30',
    purple: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    indigo: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/30',
    gray: 'bg-gray-500/10 text-gray-600 border-gray-500/30',
  };
  return colorMap[color] || colorMap.gray;
}

// ─────────────────────────────────────────────────────────────────
// Notification Item Component
// ─────────────────────────────────────────────────────────────────

function NotificationItem({ notification, onMarkRead, onDelete }) {
  const config = getNotificationConfig(notification.type);
  const IconComponent = config.icon;
  const colorClasses = getColorClasses(config.color);
  
  return (
    <div 
      className={`relative p-4 border-b last:border-b-0 transition-colors ${
        notification.is_read ? 'bg-background' : 'bg-primary/5'
      }`}
      data-testid={`notification-item-${notification.id}`}
    >
      {/* Unread indicator */}
      {!notification.is_read && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary" />
      )}
      
      <div className="flex items-start gap-3 pl-4">
        {/* Icon */}
        <div className={`p-2 rounded-lg border ${colorClasses}`}>
          <IconComponent className="h-4 w-4" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`text-sm font-medium ${notification.is_read ? 'text-muted-foreground' : ''}`}>
              {notification.title}
            </h4>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 -mr-2">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!notification.is_read && (
                  <DropdownMenuItem onClick={() => onMarkRead(notification.id)}>
                    <Check className="h-4 w-4 mr-2" />
                    Mark as read
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onDelete(notification.id)} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
            {notification.message}
          </p>
          
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground">
              {getTimeAgo(notification.created_at)}
            </span>
            {notification.priority === 'HIGH' || notification.priority === 'URGENT' && (
              <Badge variant="destructive" className="text-xs px-1.5 py-0">
                {notification.priority}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────

export default function MobileNotificationsPage() {
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unread: 0, unreadByCategory: {} });
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Fetch notifications
  const fetchNotifications = useCallback(async (resetPage = false) => {
    try {
      const currentPage = resetPage ? 1 : page;
      const res = await api.get('/mobile/notifications', {
        params: { filter: activeFilter, page: currentPage, pageSize: 20 }
      });
      
      const newItems = res.data.items || [];
      
      if (resetPage) {
        setNotifications(newItems);
        setPage(1);
      } else {
        setNotifications(prev => [...prev, ...newItems]);
      }
      
      setHasMore(newItems.length === 20);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      toast.error('Failed to load notifications');
    }
  }, [activeFilter, page]);
  
  // Fetch counts
  const fetchCounts = useCallback(async () => {
    try {
      const res = await api.get('/mobile/notifications/counts');
      setCounts(res.data.data || { total: 0, unread: 0, unreadByCategory: {} });
    } catch (err) {
      console.error('Failed to fetch counts:', err);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchNotifications(true), fetchCounts()]);
      setLoading(false);
    };
    loadData();
  }, [activeFilter]);
  
  // Refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchNotifications(true), fetchCounts()]);
    setRefreshing(false);
    toast.success('Notifications refreshed');
  };
  
  // Load more
  const handleLoadMore = async () => {
    setPage(prev => prev + 1);
    await fetchNotifications(false);
  };
  
  // Mark as read
  const handleMarkRead = async (id) => {
    try {
      await api.post(`/mobile/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
      setCounts(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
      toast.success('Marked as read');
    } catch (err) {
      toast.error('Failed to mark as read');
    }
  };
  
  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await api.post('/mobile/notifications/read-all', null, {
        params: { filter: activeFilter }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      await fetchCounts();
      toast.success('All marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };
  
  // Delete notification
  const handleDelete = async (id) => {
    try {
      await api.delete(`/mobile/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      await fetchCounts();
      toast.success('Notification deleted');
    } catch (err) {
      toast.error('Failed to delete notification');
    }
  };
  
  // Clear all read
  const handleClearRead = async () => {
    try {
      const res = await api.delete('/mobile/notifications', {
        params: { filter: activeFilter, onlyRead: true }
      });
      await fetchNotifications(true);
      await fetchCounts();
      toast.success(`${res.data.data?.deletedCount || 0} notifications cleared`);
    } catch (err) {
      toast.error('Failed to clear notifications');
    }
  };
  
  // Change filter
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setPage(1);
    setNotifications([]);
  };
  
  const unreadInCurrentFilter = activeFilter === 'ALL' 
    ? counts.unread 
    : counts.unreadByCategory?.[activeFilter] || 0;

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-notifications-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-primary text-primary-foreground px-4 py-3">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-primary-foreground hover:bg-primary-foreground/10" 
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              <h1 className="text-lg font-bold">Notifications</h1>
              {counts.unread > 0 && (
                <Badge variant="secondary" className="bg-white/20 text-white">
                  {counts.unread}
                </Badge>
              )}
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-primary-foreground hover:bg-primary-foreground/10"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-background border-b sticky top-[60px] z-10">
        <div className="flex overflow-x-auto px-2 py-2 gap-1 no-scrollbar">
          {FILTER_TABS.map(({ id, label, icon: Icon }) => {
            const unreadCount = id === 'ALL' 
              ? counts.unread 
              : counts.unreadByCategory?.[id] || 0;
            
            return (
              <button
                key={id}
                onClick={() => handleFilterChange(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeFilter === id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                data-testid={`filter-${id.toLowerCase()}`}
              >
                <Icon className="h-4 w-4" />
                {label}
                {unreadCount > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeFilter === id ? 'bg-white/20' : 'bg-primary/10 text-primary'
                  }`}>
                    {unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Actions Bar */}
      {notifications.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
          <span className="text-sm text-muted-foreground">
            {unreadInCurrentFilter} unread
          </span>
          <div className="flex items-center gap-2">
            {unreadInCurrentFilter > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs"
                onClick={handleMarkAllRead}
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs text-muted-foreground"
              onClick={handleClearRead}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Clear read
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <BellOff className="h-16 w-16 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground">No notifications</h3>
            <p className="text-sm text-muted-foreground/70 text-center mt-1">
              {activeFilter === 'ALL' 
                ? "You're all caught up!"
                : `No ${activeFilter.toLowerCase()} notifications`}
            </p>
          </div>
        ) : (
          <>
            <Card className="mx-4 mt-4 overflow-hidden">
              <CardContent className="p-0">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={handleMarkRead}
                    onDelete={handleDelete}
                  />
                ))}
              </CardContent>
            </Card>
            
            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center py-4">
                <Button 
                  variant="outline" 
                  onClick={handleLoadMore}
                  data-testid="load-more-btn"
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}
